import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import styled from 'styled-components';

import { MediaWithErrorHandling } from '../common/MediaWithErrorHandling';
import SpinningLoading from '../SpinningLoading';

import type { ElementState } from '../../renderer/ElementState';
import type { Story } from '../../types.ts/story';
import type { FC } from 'react';

interface ImagePresetProps {
  activeElement: ElementState;
  parentCompositionElement?: ElementState;
  url: string;
  story?: Story;
  onSourceChange?: (newUrl: string) => void;
  isImageControl?: boolean;
  isLogoControl?: boolean;
  isVideoControl?: boolean;
  isHeadshotControl?: boolean;
}

export const ImagePreset: FC<ImagePresetProps> = props => {
  const videoCreator = useVideoCreatorStore();

  // videoCreator.getImageElement doesn't check if the argument is an image element,
  // it just checks if it is a composition element and gets a child image. if it's not
  // a composition, it returns the element itself. THEREFORE, this works for video
  // elements too, which is why it's working in VideoControl. We should make
  // this cleaner later.
  //
  const currentElement = videoCreator.getImageElement(props.activeElement);
  const url = currentElement.source.source;

  const getDescription = () => {
    if (currentElement.source.type === 'image') {
      const photoAssets = [
        ...(videoCreator.story?.storyArtifacts || []),
        ...(videoCreator.story?.storyAssets || []),
        ...(videoCreator.story?.aiPhotos || []),
        ...(videoCreator.organization?.organizationArtifacts || []),
        ...(videoCreator.organization?.organizationLogos || []),
      ];
      return photoAssets.find(
        p =>
          p.responsiveImage?.srcSet === url ||
          p.responsiveImage?.src === url ||
          p.url === url,
      )?.title;
    }
    return '';
  };

  const origin =
    props.isImageControl || props.isLogoControl || props.isVideoControl
      ? 'composition-settings'
      : undefined;

  return (
    <>
      <Main>
        {videoCreator.savingStockPhoto && (
          <SpinningLoading text="Saving stock image…" />
        )}
        <ImageWrapper>
          <ImageOverlay />
          {props.isVideoControl ? (
            <MediaWithErrorHandling as={Video} src={props.url} />
          ) : (
            <MediaWithErrorHandling as={Image} src={url} alt="image preset" />
          )}
          <ReplaceButton
            onClick={() =>
              (videoCreator.openPhotoElementReplacementModal = {
                element: props.activeElement,
                parentCompositionElement: props.parentCompositionElement,
                onImageSourceChange: props.onSourceChange,
                isLogoControl: props.isLogoControl,
                isVideoControl: props.isVideoControl || false,
                isHeadshotControl: props.isHeadshotControl,
                // No title: the modal derives "Replace Logo" / "Replace Video"
                // / "Replace Headshot" / "Replace Image" from these flags.
                origin,
              })
            }
          >
            Replace
          </ReplaceButton>
        </ImageWrapper>

        <Description>{getDescription()}</Description>
      </Main>
    </>
  );
};

const Main = styled.div`
  position: relative;
  // margin: 15px 0;
  // height: 80px;
  // background-size: contain;
  // background-repeat: no-repeat;
  // background-position: center;
  border-radius: var(--radius-input);
  display: flex;
  gap: 10px;
  align-items: center;
`;

const Image = styled.img`
  width: 107px;
  height: 70px;
  border-radius: inherit;
  background-size: cover;
  object-fit: cover;

  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 40px;
    background: linear-gradient(
      to bottom,
      rgba(0, 0, 0, 0),
      rgba(0, 0, 0, 50%)
    );
  }
`;

const Video = styled.video`
  width: 107px;
  height: 70px;
  border-radius: inherit;

  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 40px;
    background: linear-gradient(
      to bottom,
      rgba(0, 0, 0, 0),
      rgba(0, 0, 0, 50%)
    );
  }
`;

const ImageWrapper = styled.div`
  width: 107px;
  height: 70px;
  border-radius: var(--radius);
  position: relative;
`;
const ImageOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  background-color: rgba(0, 0, 0, 0.3);
`;

const ReplaceButton = styled.button`
  outline: none;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 4px;
  padding: 5px 10px;
  position: absolute;
  color: white;
  font-family: var(--font-primary);
  font-weight: var(--font-weight-medium);
  font-size: var(--text-xs);
  text-align: center;
  background-color: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1;
  cursor: pointer;
  white-space: nowrap;
  transition: all var(--transition-normal) ease-out;

  &:hover {
    background-color: rgba(255, 255, 255, 0.25);
    border-color: rgba(255, 255, 255, 0.5);
  }
`;

const Description = styled.span`
  color: var(--text-secondary);
  font-weight: var(--font-weight-light);
  font-size: var(--text-xs);
  line-height: 12px;
`;
